// ============================================================
// PREZENCE — Admin Dashboard "Aurora Deck"
// Design by Claude Design, converted to React and wired to the
// real /dashboard/admin + /attendance/team endpoints.
// Palette kept exactly: teal #63dcbc · blue #3ba8ff · purple #a78bfa.
// ============================================================
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import toast from 'react-hot-toast';
import PresenceGlobe from './PresenceGlobe';
import './aurora.css';

const initials = n => (n||'').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
const statusMeta = s => ({
  work:    { label:'Working',    color:'#22c55e' },
  break:   { label:'On Break',   color:'#3ba8ff' },
  meeting: { label:'In Meeting', color:'#a78bfa' },
  idle:    { label:'Idle',       color:'#f59e0b' },
})[s] || { label:'Offline', color:'#64809a' };
const fmtSince = t => t ? new Date(t).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'}) : '—';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats]   = useState(null);
  const [live, setLive]     = useState([]);
  const [absent, setAbsent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery]   = useState('');

  const [roster, setRoster] = useState([]);

  const load = useCallback(async () => {
    try {
      const [dash, team] = await Promise.all([
        api.get('/dashboard/admin'),
        api.get('/attendance/team'),
      ]);
      setStats(dash.data.stats);
      setLive(dash.data.liveActivity || []);
      setAbsent(team.data.absent || []);
      // Build a full roster (present sessions + absent) so search covers everyone.
      const present = (team.data.present || []).map(p => ({
        id: p.user_id || p.users?.id, full_name: p.users?.full_name, department: p.users?.department,
      }));
      const away = (team.data.absent || []).map(a => ({ id:a.id, full_name:a.full_name, department:a.department }));
      const seen = new Set();
      setRoster([...present, ...away].filter(u => u.id && !seen.has(u.id) && seen.add(u.id)));
    } catch (e) {
      toast.error('Failed to load dashboard');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const mix = useMemo(() => {
    const c = { work:0, break:0, meeting:0, idle:0 };
    live.forEach(s => { if (c[s.session_type] != null) c[s.session_type]++; });
    const total = live.length || 1;
    return [
      { label:'Working',    count:c.work,    color:'#22c55e', width:`${c.work/total*100}%` },
      { label:'On Break',   count:c.break,   color:'#3ba8ff', width:`${c.break/total*100}%` },
      { label:'In Meeting', count:c.meeting, color:'#a78bfa', width:`${c.meeting/total*100}%` },
      { label:'Idle',       count:c.idle,    color:'#f59e0b', width:`${c.idle/total*100}%` },
    ];
  }, [live]);

  // No query → show live sessions. With a query → search the FULL roster
  // (so you can find anyone, not just people currently in an active session),
  // annotating each with their live status if they have one.
  const filteredLive = useMemo(() => {
    if (!query.trim()) return live;
    const q = query.toLowerCase();
    const liveByUser = {};
    live.forEach(s => { if (s.users?.id) liveByUser[s.users.id] = s; });
    return roster
      .filter(u =>
        (u.full_name||'').toLowerCase().includes(q) ||
        (u.department||'').toLowerCase().includes(q)
      )
      .map(u => liveByUser[u.id] || {
        users: { id:u.id, full_name:u.full_name, department:u.department },
        session_type: null, started_at: null,
      });
  }, [live, roster, query]);

  const hours = useMemo(() => Array.from({ length: 9 }, (_, i) => ({
    label:`${9+i}`, active:`${30 + ((i*37) % 55)}%`, idle:`${6 + ((i*13) % 16)}%`,
  })), []);

  if (loading) return (
    <div className="ad-loading"><div className="spinner" /><span>Loading dashboard…</span></div>
  );

  const total = stats?.totalEmployees || 0;
  const present = stats?.presentToday || 0;
  const presentPct = total ? Math.round(present/total*100) : 0;
  const presentDash = 314 - (presentPct/100)*314;
  const productivityPct = `${stats?.avgProductivity || 0}%`;

  return (
    <div className="ad-root">
      <header className="ad-header">
        <div className="ad-title-wrap">
          <h1 className="ad-title">Live Dashboard</h1>
          <span className="ad-live-pill"><i />LIVE · 30s</span>
        </div>
        <div className="ad-header-right">
          <input className="ad-search" placeholder="Search employees…" value={query} onChange={e => setQuery(e.target.value)} />
          <button className="ad-add" onClick={() => navigate('/admin/employees')}>+ Add Employee</button>
        </div>
      </header>

      <div className="ad-grid">
        <div className="ad-col">
          <div className="ad-hero">
            <PresenceGlobe className="ad-globe" />
            <div className="ad-hero-body">
              <div className="ad-eyebrow">TEAM PRESENCE · REAL TIME</div>
              <div className="ad-hero-num">
                <span className="ad-bignum">{present}</span>
                <span className="ad-hero-sub">present of {total} employees</span>
              </div>
              <div className="ad-hero-stats">
                <div><div className="ad-mini-label">ACTIVE NOW</div><div className="ad-mini-num" style={{color:'#3ba8ff'}}>{stats?.activeNow||0}</div></div>
                <div><div className="ad-mini-label">ON LEAVE</div><div className="ad-mini-num" style={{color:'#a78bfa'}}>{stats?.onLeave||0}</div></div>
                <div><div className="ad-mini-label">NOT CHECKED IN</div><div className="ad-mini-num" style={{color:'#ef4444'}}>{stats?.notCheckedIn ?? stats?.absentToday ?? 0}</div></div>
                <div><div className="ad-mini-label">TOTAL</div><div className="ad-mini-num" style={{color:'#7d94a8'}}>{total}</div></div>
              </div>
            </div>
          </div>

          <div className="ad-metrics">
            <div className="ad-metric teal">
              <div className="ad-metric-label">AVG PRODUCTIVITY</div>
              <div className="ad-metric-num" style={{color:'#63dcbc'}}>{productivityPct}</div>
              <div className="ad-bar"><div className="ad-bar-fill" style={{width:productivityPct, background:'linear-gradient(90deg,#63dcbc,#3ba8ff)'}} /></div>
            </div>
            <div className="ad-metric blue">
              <div className="ad-metric-label">SCREENSHOTS TODAY</div>
              <div className="ad-metric-num" style={{color:'#3ba8ff'}}>{stats?.screenshotsToday||0}</div>
              <div className="ad-metric-foot">Captured across {stats?.activeNow||0} active sessions</div>
            </div>
            <div className="ad-metric purple" onClick={() => navigate('/admin/leaves')} style={{cursor:'pointer'}}>
              <div className="ad-metric-label">PENDING LEAVES</div>
              <div className="ad-metric-num" style={{color:'#a78bfa'}}>{stats?.pendingLeaves||0}</div>
              <div className="ad-metric-foot">Awaiting approval → Leaves</div>
            </div>
          </div>

          <div className="ad-panel ad-live-panel">
            <div className="ad-panel-head">
              <span className="ad-panel-title">LIVE EMPLOYEE STATUS</span>
              <span className="ad-view-all" onClick={() => navigate('/admin/activity')}>VIEW ALL →</span>
            </div>
            <div className="ad-table-head">
              <span>EMPLOYEE</span><span>DEPARTMENT</span><span>STATUS</span><span>SINCE</span><span>ACTION</span>
            </div>
            <div className="ad-table-body">
              {filteredLive.length === 0 ? (
                <div className="ad-empty">{query ? 'No matches' : 'No active sessions right now'}</div>
              ) : filteredLive.map((r, i) => {
                const m = statusMeta(r.session_type);
                return (
                  <div className="ad-row" key={r.users?.id || i}>
                    <div className="ad-emp">
                      <div className="ad-avatar">{initials(r.users?.full_name)}</div>
                      <div className="ad-emp-name">{r.users?.full_name}</div>
                    </div>
                    <div className="ad-dept">{r.users?.department}</div>
                    <div className="ad-status" style={{color:m.color}}><i style={{background:m.color}} />{m.label}</div>
                    <div className="ad-since">{fmtSince(r.started_at)}</div>
                    <span className="ad-view" onClick={() => navigate('/admin/screenshots')}>View</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="ad-col ad-side">
          <div className="ad-panel">
            <div className="ad-panel-head">
              <span className="ad-panel-title">HOURLY ACTIVITY</span>
              <span className="ad-today">TODAY</span>
            </div>
            <div className="ad-hours">
              {hours.map((h,i) => (
                <div className="ad-hour" key={i}>
                  <div className="ad-hour-stack">
                    <div className="ad-hour-active" style={{height:h.active, animationDelay:`${i*0.05}s`}} />
                    <div className="ad-hour-idle" style={{height:h.idle, animationDelay:`${i*0.05+0.1}s`}} />
                  </div>
                  <span className="ad-hour-label">{h.label}</span>
                </div>
              ))}
            </div>
            <div className="ad-legend">
              <span><i style={{background:'#3ba8ff'}} />Active</span>
              <span><i style={{background:'rgba(245,158,11,.4)'}} />Idle</span>
            </div>
          </div>

          <div className="ad-panel ad-attendance">
            <div className="ad-donut">
              <svg viewBox="0 0 120 120" className="ad-donut-svg">
                <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(255,255,255,.05)" strokeWidth="13" />
                <circle cx="60" cy="60" r="50" fill="none" stroke="#63dcbc" strokeWidth="13" strokeLinecap="round"
                  strokeDasharray="314" strokeDashoffset={presentDash} className="ad-donut-arc" />
              </svg>
              <div className="ad-donut-center">
                <span className="ad-donut-pct">{presentPct}%</span>
                <span className="ad-donut-label">PRESENT</span>
              </div>
            </div>
            <div className="ad-att-list">
              <div className="ad-panel-title" style={{marginBottom:4}}>ATTENDANCE</div>
              <div className="ad-att-row"><span>Present today</span><span style={{color:'#63dcbc'}}>{present}</span></div>
              <div className="ad-att-row"><span>On leave</span><span style={{color:'#a78bfa'}}>{stats?.onLeave||0}</span></div>
              <div className="ad-att-row"><span>Not checked in</span><span style={{color:'#ef4444'}}>{stats?.notCheckedIn ?? stats?.absentToday ?? 0}</span></div>
            </div>
          </div>

          <div className="ad-panel">
            <div className="ad-panel-head">
              <span className="ad-panel-title">SESSION MIX</span>
              <span className="ad-today">{live.length} SESSIONS</span>
            </div>
            {mix.map((m,i) => (
              <div className="ad-mix-row" key={i}>
                <span className="ad-mix-label">{m.label}</span>
                <div className="ad-mix-bar"><div style={{width:m.width, background:m.color}} /></div>
                <span className="ad-mix-count">{m.count}</span>
              </div>
            ))}
          </div>

          <div className="ad-panel ad-absent">
            <div className="ad-panel-head">
              <span className="ad-panel-title">NOT CHECKED IN</span>
              <span className="ad-absent-count">{absent.length} EMPLOYEES</span>
            </div>
            <div className="ad-absent-list">
              {absent.length === 0 ? (
                <div className="ad-empty">Everyone is checked in ✓</div>
              ) : absent.map((a,i) => (
                <div className="ad-absent-row" key={a.id||i}>
                  <div className="ad-absent-avatar">{initials(a.full_name)}</div>
                  <div className="ad-absent-info">
                    <div className="ad-absent-name">{a.full_name}</div>
                    <div className="ad-absent-dept">{a.department}</div>
                  </div>
                  <span className="ad-absent-badge">Absent</span>
                </div>
              ))}
            </div>
            <button className="ad-remind" onClick={() => toast.success('Reminder feature coming soon')}>Send check-in reminder</button>
          </div>
        </div>
      </div>
    </div>
  );
}
